import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { FileText, Eye } from "lucide-react";
import type { Deal } from "@shared/schema";
import { buildNavigationUrl, type NavigationContext } from "@/lib/breadcrumbs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DealQuickViewDrawer } from "@/components/deal-quick-view-drawer";

interface LinkedDealsTableProps {
  applicantId: string;
  applicantType: "contact" | "account";
  sourceFrom?: NavigationContext;
}

const TRANSACTION_STAGE_COLORS: Record<string, string> = {
  scenario: "bg-slate-500",
  deal: "bg-blue-500",
  funded_loan: "bg-green-500",
};

const TRANSACTION_STAGE_LABELS: Record<string, string> = {
  scenario: "Scenario",
  deal: "Deal",
  funded_loan: "Funded Loan",
};

export function LinkedDealsTable({ applicantId, applicantType, sourceFrom }: LinkedDealsTableProps) {
  const [quickViewDealId, setQuickViewDealId] = useState<string | null>(null);

  const { data: deals = [], isLoading } = useQuery<Deal[]>({
    queryKey: ['/api/crm/deals', { applicantId, applicantType }],
    enabled: !!applicantId,
  });

  const formatCurrency = (value: string | null) => {
    if (!value) return "-";
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <p className="text-sm text-muted-foreground">Loading deals...</p>
      </div>
    );
  }

  if (deals.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <FileText className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-1">No deals yet</p>
        <p className="text-xs text-muted-foreground">
          Click "New Deal" to create the first deal for this {applicantType}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-xs font-medium px-2 py-1">Deal Name</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">Type</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">Stage</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 text-right">Amount Requested</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 text-right">Amount Approved</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1">Settlement Date</TableHead>
              <TableHead className="text-xs font-medium px-2 py-1 w-[60px]">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {deals.map((deal) => (
              <TableRow
                key={deal.id}
                className="h-10 hover:bg-muted/50 cursor-pointer"
                onClick={() => setQuickViewDealId(deal.id)}
                data-testid={`row-deal-${deal.id}`}
              >
                <TableCell className="font-medium px-2 py-1" data-testid={`text-deal-name-${deal.id}`}>
                  <Link
                    href={sourceFrom ? buildNavigationUrl(`/deals/${deal.id}`, sourceFrom) : `/deals/${deal.id}`}
                    className="text-primary hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {deal.dealName}
                  </Link>
                </TableCell>
                <TableCell className="px-2 py-1">
                  <Badge variant="outline" className="capitalize text-xs">
                    {deal.dealType?.replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell className="px-2 py-1">
                  <Badge
                    className={`${TRANSACTION_STAGE_COLORS[deal.transactionStage]} text-white text-xs`}
                    data-testid={`badge-stage-${deal.id}`}
                  >
                    {TRANSACTION_STAGE_LABELS[deal.transactionStage]}
                  </Badge>
                </TableCell>
                <TableCell className="text-right px-2 py-1" data-testid={`text-amount-requested-${deal.id}`}>
                  {formatCurrency(deal.amountRequested)}
                </TableCell>
                <TableCell className="text-right px-2 py-1" data-testid={`text-amount-approved-${deal.id}`}>
                  {formatCurrency(deal.amountApproved)}
                </TableCell>
                <TableCell className="px-2 py-1" data-testid={`text-settlement-date-${deal.id}`}>
                  {formatDate(deal.expectedSettlementDate)}
                </TableCell>
                <TableCell className="px-2 py-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      setQuickViewDealId(deal.id);
                    }}
                    data-testid={`button-view-deal-${deal.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <DealQuickViewDrawer
        dealId={quickViewDealId}
        open={quickViewDealId !== null}
        onOpenChange={(open) => {
          if (!open) setQuickViewDealId(null);
        }}
      />
    </>
  );
}
